import { linkOptions } from '@tanstack/react-router';

import { SupplierCompanyMessage } from '../../../../../types/SupplierCompanyMessage';
import { TableBuilder } from '../../../../cdl/TableBuilder/TableBuilder';
import { TableBuilderColumn } from '../../../../cdl/TableBuilder/TableBuilderColumn';
import { GenericError } from '../../../../common/Error/GenericError';
import { formatCompanyName } from '../../../../common/helpers/formatCompanyName.helper';
import { formatDateTime } from '../../../../common/helpers/formatDateTime.helper';
import { translate } from '../../../../common/helpers/translate.helper';
import { useRole } from '../../../../common/hooks/useRole';
import { IconCheck } from '../../../../common/icons/cdl/Check';
import { LoadingIndicator } from '../../../../common/LoadingIndicator/LoadingIndicator';
import { TableEmptyState } from '../../../../common/TableEmptyState/TableEmptyState';
import { useSupplierList } from '../hooks/useSupplierList';

interface SuppliersTableProps {
    groupId: string;
}

export const SuppliersTable = ({ groupId }: SuppliersTableProps) => {
    const suppliersQuery = useSupplierList({ groupId });
    const role = useRole();

    if (suppliersQuery.isPending) {
        return <LoadingIndicator />;
    }

    if (suppliersQuery.isError) {
        return <GenericError />;
    }

    if (!suppliersQuery.data.length) {
        return <TableEmptyState text={translate('suppliers.emptylist')} />;
    }

    const reversedSuppliers = [...suppliersQuery.data].reverse();

    return (
        <TableBuilder
            data={reversedSuppliers}
            rowLink={(row: SupplierCompanyMessage) => {
                return linkOptions({
                    to: '/supplier-groups/$groupId/companies/$companyId',
                    params: { groupId: row.supplierGroupId, companyId: row.id },
                });
            }}
            grow={false}
        >
            <TableBuilderColumn<SupplierCompanyMessage> header={translate('customer.name')} width="30%" truncate>
                {(row) => formatCompanyName({ company: row, forcePublicName: true })}
            </TableBuilderColumn>
            <TableBuilderColumn<SupplierCompanyMessage> header={translate('company.internalName')} width="20%" truncate>
                {(row) => row.internalName || null}
            </TableBuilderColumn>
            <TableBuilderColumn<SupplierCompanyMessage> header={translate('global.email')} width="25%" truncate>
                {(row) => row.email}
            </TableBuilderColumn>
            <TableBuilderColumn<SupplierCompanyMessage> header={translate('customer.datecreated')}>
                {(row) => formatDateTime({ date: row.dateCreated })}
            </TableBuilderColumn>

            {role.isSuperAdmin() ? (
                <TableBuilderColumn<SupplierCompanyMessage> header={translate('customer.testcompany')}>
                    {(row) => (row.testCompany ? <IconCheck width={16} height={16} /> : null)}
                </TableBuilderColumn>
            ) : null}
        </TableBuilder>
    );
};
